/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { Switch, Route, Router, Redirect } from 'react-router-dom';

import { GlobalStyle } from 'styles/global-styles';

import { NotFoundPage } from './components/NotFoundPage/Loadable';
import { useTranslation } from 'react-i18next';
import { PortfoliosPage } from './pages/PortfoliosPage/Loadable';
import UserPageRouter from './components/UserPageRouter';
import { LoginPage } from './pages/LoginPage/Loadable';
import { CompanyDetailPage } from './pages/CompanyDetailPage/Loadable';
import { RequireAuth } from './components/RequireAuth';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';
import { Menu } from './components/Menu';
import { useAuth0 } from '@auth0/auth0-react';
import { Authorization } from 'utils/requestInterceptor';
import { UrlFragments } from './constants';
import { usePreloadState } from './helpers/PreloadStateHook';
import UserPagesRouter from './components/UserPagesRouter';
import { SubscriptionsPage } from './pages/SubscriptionsPage/Loadable';
import { OverviewPage } from './pages/OverviewPage/Loadable';
import SubscriptionDetailPageRouter from './components/SubscriptionDetailPageRouter';
import { CreateSubscriptionPage } from './pages/CreateSubscriptionPage/Loadable';
import { UnauthorisedPage } from './pages/UnauthorisedPage';
import { history } from 'utils/history';
import { useAuthorisationHandler } from 'app/helpers/UseUnauthorisedHandler';
import { makeThemeStyle } from 'styles/makeThemeStyle';
import { CompaniesPage } from './pages/CompaniesPage/Loadable';
import CompanyPagesRouter from './components/CompanyPagesRouter';

const useStyles = makeThemeStyle(mTheme => ({
  root: {
    display: 'flex',
    height: '100vh',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: mTheme.spacing(0, 1),
    // necessary for content to be below app bar
    ...(mTheme.mixins.toolbar as React.CSSProperties),
  },
  content: {
    flexGrow: 1,
    padding: mTheme.spacing(3),
    paddingBottom: 0,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const {
  match: { byId, byTab },
  user: { all_users, user },
  portfolio: { all_pfs },
  subscription: { all_subscription, subscription, create_subscription },
  company: { all_companies, company, create_company },
  login,
  overview,
  unauthorised,
} = UrlFragments;

export const RedirectToOverview = () => <Redirect to={`/${overview}`} />;

export function App() {
  const { i18n } = useTranslation();
  const { isLoading, error, isAuthenticated, getAccessTokenSilently } =
    useAuth0();

  const classes = useStyles();
  const [isTokenSet, setIsTokenSet] = React.useState(false);

  React.useEffect(() => {
    async function setUpToken() {
      const token = await getAccessTokenSilently();
      Authorization.accessToken = token;
      setIsTokenSet(!!Authorization.accessToken);
    }

    if (isAuthenticated) setUpToken();
  }, [isAuthenticated, getAccessTokenSilently, isTokenSet]);

  usePreloadState(isTokenSet);
  useAuthorisationHandler(isTokenSet, isAuthenticated);

  if (error) {
    return <div>Oops... {error.message}</div>;
  }

  if (isLoading || (isAuthenticated && !isTokenSet)) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className={classes.root}>
        <Router history={history}>
          {!isAuthenticated || <Menu />}
          <Helmet
            titleTemplate="%s - Predictor Provision"
            defaultTitle="Predictor Provision"
            htmlAttributes={{ lang: i18n.language }}
          >
            <meta name="description" content="Predictor Provisioning Tool" />
          </Helmet>
          <main className={classes.content}>
            <div className={classes.toolbar} />
            <Switch>
              <Route
                path={`/${overview}`}
                exact
                render={() => (
                  <RequireAuth>
                    <OverviewPage />
                  </RequireAuth>
                )}
              />
              <Route
                path={`/${all_pfs}`}
                exact
                render={() => (
                  <RequireAuth>
                    <PortfoliosPage />
                  </RequireAuth>
                )}
              />
              <Route
                path={`/${all_companies}`}
                exact
                render={() => (
                  <RequireAuth>
                    <CompaniesPage />
                  </RequireAuth>
                )}
              />
              <Route
                path={`/${all_users}`}
                exact
                render={() => (
                  <RequireAuth>
                    <UserPageRouter />
                  </RequireAuth>
                )}
              />
              <Route
                path={`/${all_subscription}`}
                exact
                render={() => (
                  <RequireAuth>
                    <SubscriptionsPage />
                  </RequireAuth>
                )}
              />
              <Route
                path={[
                  `/${user}/${byId}/${create_subscription}`,
                  `/${create_subscription}`,
                ]}
                strict
                render={() => (
                  <RequireAuth>
                    <CreateSubscriptionPage />
                  </RequireAuth>
                )}
              />
              <Route
                path={`/${user}/${byId}/${byTab}?`}
                strict
                render={() => (
                  <RequireAuth>
                    <UserPagesRouter />
                  </RequireAuth>
                )}
              />
              <Route
                path={`/${company}/${byId}`}
                strict
                render={() => (
                  <RequireAuth>
                    <CompanyPagesRouter />
                  </RequireAuth>
                )}
              />
              <Route
                path={`/${create_company}`}
                strict
                render={() => (
                  <RequireAuth>
                    <CompanyPagesRouter creationMode />
                  </RequireAuth>
                )}
              />
              <Route
                path={`/${subscription}/${byId}/${byTab}?`}
                strict
                render={() => (
                  <RequireAuth>
                    <SubscriptionDetailPageRouter />
                  </RequireAuth>
                )}
              />
              <Route path={`/${login}`} exact>
                <LoginPage />
              </Route>
              <Route exact path="/" render={RedirectToOverview} />
              <Route exact path={`/${unauthorised}`}>
                <UnauthorisedPage />
              </Route>
              <Route>
                <NotFoundPage />
              </Route>
            </Switch>
          </main>
          <GlobalStyle />
        </Router>
      </div>
    </>
  );
}
